import React, { useEffect, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useAnalytics } from 'use-analytics';
import withSpinner from '../../../../HOCs/withSpinner';
import HotelItinerary from '../HotelItinerary';
import { SearchModeLabel, SearchModes, SearchStatus } from '../../../../constants';
import Warning from '../../../../assets/svgs/Warning.svg';
import { setIsUserInteractedWithHotelList } from '../../../../store/slices/hotel';
import checkIsSelectedHotel from '../../../../utils/checkIsSelectedHotel';
import TravelBlurbs from '../../../TravelBlurbs';

const HotelList = ({
  onViewMoreClick,
  isInsideModal,
  tripId,
  onModalClose,
  hotelToScroll,
  onHotelItineraryMouseEnter,
  onHotelItineraryMouseLeave,
  doShowChatGpt,
  doExcludeSelectedHotel,
  travelOption,
}) => {
  const dispatch = useDispatch();
  const hotelSearchState = useSelector((state) => state.hotel.hotelSearches[tripId] || {});
  const trip = useSelector((store) => store.trip.selectedTrips[tripId]);
  const responses = useSelector((state) => state.hotel.hotelSearchResponses);
  const [response, setResponse] = useState(null);
  const { track } = useAnalytics();
  const { hasUserInteractedWithHotelList } = useSelector((store) => store.hotel);

  const handleHotelItineraryMouseEnter = (key, index) => () => {
    if (index >= 4 && !hasUserInteractedWithHotelList) {
      dispatch(setIsUserInteractedWithHotelList(true));
    }
    if (onHotelItineraryMouseEnter) {
      onHotelItineraryMouseEnter(key);
    }
  };

  const handleHotelItineraryMouseLeave = (key, index) => {
    if (index >= 4 && !hasUserInteractedWithHotelList) {
      dispatch(setIsUserInteractedWithHotelList(true));
    }
    if (onHotelItineraryMouseLeave) {
      onHotelItineraryMouseLeave(key);
    }
  };

  useEffect(() => {
    if (hotelSearchState.hotelResults && responses) {
      if (trip && trip.hotelItineraries && Object.keys(trip.hotelItineraries).length > 0) {
        setResponse(() => ({
          ...responses[hotelSearchState.queryString],
          hotels:
            responses[hotelSearchState.queryString] &&
            responses[hotelSearchState.queryString].hotels.filter(
              (item) =>
                !doExcludeSelectedHotel || !checkIsSelectedHotel(item, trip.hotelItineraries),
            ),
        }));
      } else {
        setResponse(responses[hotelSearchState.queryString]);
      }
      track('hotelList.gotHotelSearchResponse', {
        queryString: hotelSearchState.queryString,
      });
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [hotelSearchState.hotelResults, trip]);

  const shouldShowSpinner =
    !hotelSearchState ||
    hotelSearchState.searchStatus === SearchStatus.INITIAL ||
    hotelSearchState.searchStatus === SearchStatus.SEARCHING;

  const renderComponent = () => (
    <>
      {hotelSearchState && hotelSearchState.searchStatus === SearchStatus.DONE && (
        <>
          {response && response.hotels && response.hotels.length ? (
            <>
              <div>
                <TravelBlurbs type={SearchModeLabel[SearchModes.HOTEL].toLocaleLowerCase()} />
                {response.hotels.map((itinerary, index) => (
                  <HotelItinerary
                    id={itinerary.id}
                    key={itinerary.id}
                    itinerary={itinerary}
                    tripId={tripId}
                    onModalClose={onModalClose}
                    isInsideModal={isInsideModal}
                    isExpanded={{
                      value: hotelToScroll.hotelKey === itinerary.hotel.hotelKey,
                    }}
                    onMouseEnter={(key) => handleHotelItineraryMouseEnter(key, index)}
                    onMouseLeave={(key) => handleHotelItineraryMouseLeave(key, index)}
                    isHotelSelected={trip && checkIsSelectedHotel(itinerary, trip.hotelItineraries)}
                    doShowChatGpt={doShowChatGpt}
                    travelOption={travelOption}
                  />
                ))}
              </div>
              <button
                onClick={onViewMoreClick}
                type="button"
                className="border border-theme block rounded-pill text-center rounded-2xl text-x text-theme py-3 mx-auto mb-5 w-64 font-bold mt-2"
              >
                {!isInsideModal && (
                  <span>
                    View more
                    <FaChevronDown className="inline-block ml-3" />
                  </span>
                )}
                {isInsideModal && (
                  <span>
                    View less
                    <FaChevronUp className="inline-block ml-3" />
                  </span>
                )}
              </button>
            </>
          ) : (
            <div className="flex items-center flex-col h-40 justify-center gap-3 text-center">
              <Warning />
              Hotels not found for your search parameters. {response ? response.error : ''}
            </div>
          )}
        </>
      )}
    </>
  );

  return withSpinner(renderComponent, shouldShowSpinner, undefined, undefined, undefined, 'mt-4');
};

HotelList.propTypes = {
  onViewMoreClick: PropTypes.func.isRequired,
  isInsideModal: PropTypes.bool,
  tripId: PropTypes.string.isRequired,
  onModalClose: PropTypes.func,
  hotelToScroll: PropTypes.shape({
    hotelKey: PropTypes.string,
  }),
  onHotelItineraryMouseEnter: PropTypes.func,
  onHotelItineraryMouseLeave: PropTypes.func,
  doShowChatGpt: PropTypes.bool,
  doExcludeSelectedHotel: PropTypes.bool,
  travelOption: PropTypes.shape({
    key: PropTypes.string,
    mode: PropTypes.number,
  }),
};

HotelList.defaultProps = {
  isInsideModal: false,
  onModalClose: undefined,
  hotelToScroll: { hotelKey: '' },
  onHotelItineraryMouseEnter: undefined,
  onHotelItineraryMouseLeave: undefined,
  doShowChatGpt: true,
  doExcludeSelectedHotel: true,
  travelOption: undefined,
};

export default React.memo(HotelList);
